import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslocoService } from '@jsverse/transloco';
import { ErrorModel } from '../../../core/models/common/error.model';
import { CHOOSE_FILE_BUTTON, DOWNLOAD_FILE_BUTTON, UPLOAD_FILE_BUTTON } from '../../constants/common-bt';

export enum statusUpload {
  PENDING = 'PENDING',
  UPLOADING = 'UPLOADING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR'
}

@Component({
  selector: 'app-import-file-a',
  standalone: false,
  templateUrl: './import-file-a.component.html',
  styleUrl: './import-file-a.component.scss'
})
export class ImportFileAComponent<T extends Record<string, unknown>> {

  STATUS_UPLOAD = statusUpload;
  @Input() currentResponse: T | null = null;
  @Input() responseFields: { key: string, label: string }[] = [];
  @Input() errorResponse: ErrorModel | null = null;

  // CANCEL_BUTTON
  btnFunctions = [CHOOSE_FILE_BUTTON, UPLOAD_FILE_BUTTON, DOWNLOAD_FILE_BUTTON];

  @Output() event: EventEmitter<string> = new EventEmitter<string>();
  @Output() fileSelected: EventEmitter<File> = new EventEmitter<File>();
  @Input() currentStatus: statusUpload = statusUpload.PENDING;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  errorsLocal: string[] = [];

  selectedFile: File | null = null;

  isDragOver = false;

  showDetailsDialog = false;

  constructor(
    public messageService: MessageService,
    private transloco: TranslocoService,
  ) { }

  emitAction(action: string | undefined) {
    if (action === CHOOSE_FILE_BUTTON.action) {
      this.fileInput.nativeElement.click();
    } else if (action === DOWNLOAD_FILE_BUTTON.action) {
      this.event.emit(DOWNLOAD_FILE_BUTTON.action);
    } else if (action === UPLOAD_FILE_BUTTON.action) {
      if (this.selectedFile && this.errorsLocal.length === 0) {
        this.newBatch();
        this.event.emit(UPLOAD_FILE_BUTTON.action);
      } else {
        this.messageService.add({ severity: 'warn', summary: this.transloco.translate('common.error'), detail: this.transloco.translate('importFile.selectValidFile') });
      }
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0 && this.validateRulesForFile(input.files[0])) {
      this.selectedFile = input.files[0];
      this.fileSelected.emit(this.selectedFile);
    }
  }

  onClickFileArea() {
    if (!this.selectedFile) {
      this.fileInput.nativeElement.click();
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0 && this.validateRulesForFile(event.dataTransfer.files[0])) {
      this.selectedFile = event.dataTransfer.files[0];
      this.fileSelected.emit(this.selectedFile);
    }
  }

  validateRulesForFile(arg0: File) {
    this.currentStatus = statusUpload.PENDING;
    const validTypes = ['text/csv'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    const maxRows = 1000;

    this.errorsLocal = [];
    if (!validTypes.includes(arg0.type)) {
      this.messageService.add({ severity: 'error', summary: this.transloco.translate('importFile.invalidType'), detail: this.transloco.translate('importFile.uploadCsv') });
      this.errorsLocal.push(this.transloco.translate('importFile.invalidFile'));
    }

    if (arg0.size > maxSize) {
      this.messageService.add({ severity: 'error', summary: this.transloco.translate('importFile.fileTooBig'), detail: this.transloco.translate('importFile.maxSizeDetail') });
      this.errorsLocal.push(this.transloco.translate('importFile.fileTooBig'));
    }

    let contLines = 0;
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n');
      contLines = lines.length;
      if (contLines > maxRows) {
        this.messageService.add({ severity: 'error', summary: this.transloco.translate('importFile.tooManyRows'), detail: this.transloco.translate('importFile.maxRowsDetail') });
        this.errorsLocal.push(this.transloco.translate('importFile.tooManyRows'));
      }
    };
    reader.readAsText(arg0);
    if (this.errorsLocal.length > 0) {
      this.currentStatus = statusUpload.ERROR;
      return false;
    }

    return true;
  }

  newBatch() {
    this.selectedFile = null;
    this.errorsLocal = [];
    this.currentResponse = null;
    this.errorResponse = null;
    this.currentStatus = statusUpload.PENDING;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  getResponseValue(path: string): unknown {
    if (!this.currentResponse) return 0;
    return path.split('.').reduce((obj: unknown, key: string) => {
      if (obj && typeof obj === 'object' && key in obj) {
        return (obj as Record<string, unknown>)[key];
      }
      return 0;
    }, this.currentResponse);
  }

  openDetailsDialog() {
    this.showDetailsDialog = true;
  }
  closeDetailsDialog() {
    this.showDetailsDialog = false;
  }

  downloadJson() {
    if (!this.currentResponse) return;
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(this.currentResponse, null, 2));
    const dlAnchorElem = document.createElement('a');
    dlAnchorElem.setAttribute("href", dataStr);
    dlAnchorElem.setAttribute("download", "reporte.json");
    dlAnchorElem.click();
  }
}
